import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { questionsApi } from '../utils/api'
import clsx from 'clsx'

interface CommandItem {
  id: string
  label: string
  description?: string
  icon: string
  action: () => void
  keywords?: string[]
  category: 'navigation' | 'action' | 'question'
}

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Fetch questions for search
  const { data: questionsData } = useQuery({
    queryKey: ['questions', 'command-palette', search],
    queryFn: () => questionsApi.list({ page: 1, page_size: 10, search: search || undefined }),
    enabled: isOpen && search.length > 1,
    staleTime: 30000,
  })

  // Navigation commands
  const navigationCommands: CommandItem[] = useMemo(() => [
    {
      id: 'nav-dashboard',
      label: 'Dashboard',
      description: 'System overview and portfolio health',
      icon: '◉',
      action: () => navigate('/'),
      keywords: ['home', 'overview', 'main'],
      category: 'navigation',
    },
    {
      id: 'nav-questions',
      label: 'Questions',
      description: 'Browse market questions',
      icon: '◎',
      action: () => navigate('/questions'),
      keywords: ['markets', 'predictions', 'list'],
      category: 'navigation',
    },
    {
      id: 'nav-runs',
      label: 'Prediction Runs',
      description: 'View prediction run history',
      icon: '▶',
      action: () => navigate('/runs'),
      keywords: ['history', 'agents'],
      category: 'navigation',
    },
    {
      id: 'nav-positions',
      label: 'Positions',
      description: 'Track active positions and P&L',
      icon: '◈',
      action: () => navigate('/positions'),
      keywords: ['portfolio', 'trades', 'pnl'],
      category: 'navigation',
    },
    {
      id: 'nav-wallets',
      label: 'Wallets',
      description: 'Monitor whale activity',
      icon: '◆',
      action: () => navigate('/wallets'),
      keywords: ['whales', 'traders'],
      category: 'navigation',
    },
    {
      id: 'nav-research',
      label: 'Research',
      description: 'Company research and analysis',
      icon: '⬡',
      action: () => navigate('/research'),
      keywords: ['companies', 'analysis'],
      category: 'navigation',
    },
    {
      id: 'nav-skills',
      label: 'Skills',
      description: 'Manage API skills and integrations',
      icon: '⚙',
      action: () => navigate('/skills'),
      keywords: ['apis', 'integrations', 'tools'],
      category: 'navigation',
    },
    {
      id: 'nav-memory',
      label: 'Memory',
      description: 'View episodic memory and learning',
      icon: '◇',
      action: () => navigate('/memory'),
      keywords: ['learning', 'history'],
      category: 'navigation',
    },
    {
      id: 'nav-forecast',
      label: 'Forecast',
      description: 'Make standalone predictions',
      icon: '🔮',
      action: () => navigate('/forecast'),
      keywords: ['predict', 'question'],
      category: 'navigation',
    },
  ], [navigate])

  // Action commands
  const actionCommands: CommandItem[] = useMemo(() => [
    {
      id: 'action-new-forecast',
      label: 'New Forecast',
      description: 'Create a new prediction',
      icon: '✨',
      action: () => navigate('/forecast'),
      keywords: ['predict', 'new', 'create'],
      category: 'action',
    },
    {
      id: 'action-high-edge',
      label: 'High Edge Questions',
      description: 'Show questions with >5% edge',
      icon: '🎯',
      action: () => navigate('/questions?filter=high_edge'),
      keywords: ['edge', 'opportunity'],
      category: 'action',
    },
    {
      id: 'action-closing-soon',
      label: 'Closing Soon',
      description: 'Markets closing within 24h',
      icon: '⏰',
      action: () => navigate('/questions?filter=closing_soon'),
      keywords: ['urgent', 'deadline'],
      category: 'action',
    },
  ], [navigate])

  // Question results from search
  const questionCommands: CommandItem[] = useMemo(() => {
    if (!questionsData?.items) return []
    return questionsData.items.map((q) => ({
      id: `question-${q.id}`,
      label: q.title,
      description: q.category || undefined,
      icon: '📊',
      action: () => navigate(`/question/${q.id}`),
      category: 'question' as const,
    }))
  }, [questionsData, navigate])

  // Filter and combine commands
  const filteredCommands = useMemo(() => {
    const searchLower = search.toLowerCase().trim()

    if (!searchLower) {
      // Show navigation and action commands when no search
      return [...navigationCommands, ...actionCommands]
    }

    const matchesSearch = (item: CommandItem) => {
      const labelMatch = item.label.toLowerCase().includes(searchLower)
      const descMatch = item.description?.toLowerCase().includes(searchLower)
      const keywordMatch = item.keywords?.some(k => k.includes(searchLower))
      return labelMatch || descMatch || keywordMatch
    }

    const navMatches = navigationCommands.filter(matchesSearch)
    const actionMatches = actionCommands.filter(matchesSearch)

    // Include question results when searching
    return [...navMatches, ...actionMatches, ...questionCommands]
  }, [search, navigationCommands, actionCommands, questionCommands])

  // Keyboard shortcut to open (cmd+k or ctrl+k)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault()
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setSearch('')
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [filteredCommands.length])

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`)
      selectedEl?.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action()
          setIsOpen(false)
        }
        break
    }
  }, [filteredCommands, selectedIndex])

  const handleSelect = (item: CommandItem) => {
    item.action()
    setIsOpen(false)
  }

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: { category: string; items: CommandItem[] }[] = []
    let currentCategory = ''

    filteredCommands.forEach(cmd => {
      if (cmd.category !== currentCategory) {
        currentCategory = cmd.category
        groups.push({ category: currentCategory, items: [] })
      }
      groups[groups.length - 1].items.push(cmd)
    })

    return groups
  }, [filteredCommands])

  // Calculate absolute index for selection
  const getAbsoluteIndex = (groupIdx: number, itemIdx: number) => {
    let idx = 0
    for (let g = 0; g < groupIdx; g++) {
      idx += groupedCommands[g].items.length
    }
    return idx + itemIdx
  }

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'navigation': return 'Navigate'
      case 'action': return 'Actions'
      case 'question': return 'Questions'
      default: return category
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-void/80 backdrop-blur-sm z-[400]"
            onClick={() => setIsOpen(false)}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.15 }}
            className="fixed top-[15%] left-1/2 -translate-x-1/2 w-full max-w-xl z-[401]"
          >
            <div className="glass-card overflow-hidden mx-4 shadow-2xl">
              {/* Search input */}
              <div className="flex items-center gap-3 p-4 border-b border-star-dim/20">
                <span className="text-star-muted" aria-hidden="true">⌘</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search commands and questions..."
                  className="flex-1 bg-transparent text-star placeholder-star-dim/50 focus:outline-none text-base"
                  aria-label="Search commands"
                  role="combobox"
                  aria-expanded="true"
                  aria-controls="command-list"
                  aria-activedescendant={filteredCommands[selectedIndex]?.id}
                />
                <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-void-100 rounded text-xs text-star-dim font-mono">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <div
                ref={listRef}
                id="command-list"
                className="max-h-[60vh] overflow-y-auto"
                role="listbox"
              >
                {filteredCommands.length === 0 ? (
                  <div className="p-8 text-center text-star-dim text-sm">
                    No results found for "{search}"
                  </div>
                ) : (
                  groupedCommands.map((group, groupIdx) => (
                    <div key={group.category}>
                      <div className="px-4 py-2 text-xs text-star-dim font-mono uppercase tracking-wider bg-void-50/50">
                        {getCategoryLabel(group.category)}
                      </div>
                      {group.items.map((item, itemIdx) => {
                        const absoluteIdx = getAbsoluteIndex(groupIdx, itemIdx)
                        const isSelected = absoluteIdx === selectedIndex

                        return (
                          <button
                            key={item.id}
                            id={item.id}
                            data-index={absoluteIdx}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => handleSelect(item)}
                            className={clsx(
                              'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                              isSelected
                                ? 'bg-signal-blue/10 text-star'
                                : 'text-star-muted hover:bg-void-50'
                            )}
                          >
                            <span className="text-lg" aria-hidden="true">{item.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{item.label}</div>
                              {item.description && (
                                <div className="text-xs text-star-dim truncate">{item.description}</div>
                              )}
                            </div>
                            {isSelected && (
                              <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 bg-void-100 rounded text-xs text-star-dim font-mono">
                                ↵
                              </kbd>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-2 border-t border-star-dim/20 flex items-center gap-4 text-xs text-star-dim">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-void-100 rounded font-mono">↑↓</kbd> navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-void-100 rounded font-mono">↵</kbd> select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-void-100 rounded font-mono">esc</kbd> close
                </span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
